import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Page } from '../../components/shell/Page';
import ActionBar from '../../components/shell/ActionBar';
import SplitLayout from '../../components/shell/SidePane';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useCSVImportPane } from './CSVImportDialog';
import {
  Plus, Search, Trash2,
  UserCheck, UserCog, Eye, Link as LinkIcon, Copy, Upload, RefreshCw
} from 'lucide-react';

export const STAGE_COLORS = {
  new: 'bg-blue-500',
  contacted: 'bg-violet-500',
  interested: 'bg-amber-500',
  demo_scheduled: 'bg-cyan-500',
  enrolled: 'bg-green-500',
  lost: 'bg-red-500',
};

export const STAGE_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  interested: 'Interested',
  demo_scheduled: 'Demo Scheduled',
  enrolled: 'Enrolled',
  lost: 'Lost',
};

export const SOURCE_LABELS = {
  website: 'Website', referral: 'Referral', social_media: 'Social Media',
  walk_in: 'Walk-in', phone: 'Phone Call', other: 'Other',
};

export default function CRMPage() {
  const { api, user: currentUser, tenantInfo } = useAuth();
  const navigate = useNavigate();
  const isInstructor = currentUser?.role === 'instructor';
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showCSVImport, setShowCSVImport] = useState(false);
  const initialLoad = useRef(true);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (searchQuery) params.set('search', searchQuery);
      if (stageFilter) params.set('stage', stageFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      const res = await api.get(`/crm/leads?${params}`);
      setLeads(res.data.leads || []);
    } catch { toast.error('Failed to load leads'); }
  }, [api, searchQuery, stageFilter, sourceFilter]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api.get('/crm/analytics');
      setAnalytics(res.data);
    } catch {}
  }, [api]);

  // Initial load (leads + analytics) — runs once; later filter changes go through the effect below
  useEffect(() => {
    if (!initialLoad.current) return;
    initialLoad.current = false;
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchAnalytics()]);
      setLoading(false);
    };
    load();
  }, [fetchLeads, fetchAnalytics]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleStageChange = async (leadId, newStage) => {
    try {
      await api.put(`/crm/leads/${leadId}/stage`, { stage: newStage });
      fetchLeads();
      fetchAnalytics();
    } catch { toast.error('Failed to update stage'); }
  };

  // Lead detail / create open in their own tabs (see LeadDetail.js / LeadForm.js)
  const openLeadDetail = (lead) => navigate(`/crm/leads/${lead.id}`);
  const openCreateLead = () => navigate('/crm/leads/new');

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await api.delete(`/crm/leads/${leadId}`);
      toast.success('Lead deleted');
      fetchLeads();
      fetchAnalytics();
    } catch { toast.error('Failed to delete lead'); }
  };

  // Group leads by stage for Kanban
  const stages = Object.keys(STAGE_LABELS);
  const leadsByStage = stages.reduce((acc, stage) => {
    acc[stage] = leads.filter(l => l.stage === stage);
    return acc;
  }, {});

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    const newStage = destination.droppableId;
    // Optimistic update
    setLeads(prev => prev.map(l => l.id === draggableId ? { ...l, stage: newStage } : l));
    try {
      await api.put(`/crm/leads/${draggableId}/stage`, { stage: newStage });
      fetchAnalytics();
    } catch {
      toast.error('Failed to update stage');
      fetchLeads();
    }
  };

  // Build public lead capture URL for current tenant
  const tenantSubdomain = tenantInfo?.subdomain;
  const publicLeadUrl = tenantSubdomain ? `${window.location.origin}/tenant/${tenantSubdomain}/enquire` : null;

  const activeFilterCount = [searchQuery, stageFilter, sourceFilter].filter(Boolean).length;

  const viewSwitcher = (
    <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="crm-view-switcher">
      {[['kanban', 'Kanban'], ['list', 'List']].map(([v, l]) => (
        <button
          key={v}
          type="button"
          onClick={() => setView(v)}
          className={`px-3 h-8 font-medium ${view === v ? 'bg-shell-primary text-white font-semibold' : 'bg-white hover:bg-muted'}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  // ─── CSV import — side pane (no popup) ───
  const csv = useCSVImportPane({
    open: showCSVImport,
    onOpenChange: setShowCSVImport,
    onImported: () => { fetchLeads(); fetchAnalytics(); },
  });
  const openCSVImport = () => setShowCSVImport(true);
  const side = showCSVImport ? {
    open: true,
    onOpenChange: csv.close,
    title: csv.title,
    subtitle: 'Upload → map columns → preview → import',
    icon: csv.icon,
    actions: csv.headerBadge,
    children: csv.body,
    footer: csv.footer,
    width: 44,
    testid: 'csv-import-dialog',
  } : null;

  // New lead / Import CSV — rendered once per mode: in the kanban ActionBar (as action objects)
  // or in the list DataGrid's toolbar (as nodes).
  const leadActionItems = [
    { key: 'new-lead', label: 'New lead', icon: Plus, onClick: openCreateLead, testid: 'add-lead-btn' },
    { key: 'import-csv', label: 'Import CSV', icon: Upload, variant: 'outline', onClick: openCSVImport, testid: 'csv-import-btn' },
  ];
  const leadActions = (
    <>
      <Button size="sm" className="h-9" onClick={openCreateLead} data-testid="add-lead-btn">
        <Plus className="w-4 h-4" /> New lead
      </Button>
      <Button size="sm" variant="outline" className="h-9" onClick={openCSVImport} data-testid="csv-import-btn">
        <Upload className="w-4 h-4" /> Import CSV
      </Button>
    </>
  );

  const clearFilters = () => { setSearchQuery(''); setStageFilter(''); setSourceFilter(''); };

  // Stage / Source filters (server-side) — shared by the kanban ActionBar and the list DataGrid
  const stageSourceFilters = (
    <>
      <div className="space-y-1">
        <Label className="text-xs">Stage</Label>
        <Select value={stageFilter || 'all'} onValueChange={(v) => setStageFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="All Stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {stages.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Source</Label>
        <Select value={sourceFilter || 'all'} onValueChange={(v) => setSourceFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="All Sources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {activeFilterCount > 0 && (
        <Button variant="ghost" size="sm" className="h-9" onClick={clearFilters}>
          Clear
        </Button>
      )}
    </>
  );

  return (
    <DashboardLayout>
      <SplitLayout side={side}>
      <Page data-testid="crm-page">
        {/* Kanban mode: same action bar as the list grid (in list mode the DataGrid renders its own) */}
        {view === 'kanban' && (
          <ActionBar
            bordered
            data-testid="crm-kanban-bar"
            title="Leads"
            rightSlot={viewSwitcher}
            tools={[
              { key: 'refresh', icon: RefreshCw, title: 'Refresh', onClick: () => { fetchLeads(); fetchAnalytics(); } },
            ]}
            search={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search leads...', testid: 'search-leads' }}
            primary={leadActionItems}
            advancedSearch={(
              <div className="flex items-end gap-3 flex-wrap">
                {stageSourceFilters}
              </div>
            )}
            activeFilterCount={activeFilterCount}
          />
        )}

        {/* Public Lead Capture URL banner */}
        {publicLeadUrl && (
          <Card className="bg-gradient-to-r from-blue-50 to-violet-50 border-blue-200" data-testid="public-lead-url-banner">
            <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <LinkIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium">Public Lead Capture Form</p>
                  <p className="text-xs text-muted-foreground truncate">{publicLeadUrl}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => { navigator.clipboard.writeText(publicLeadUrl); toast.success('Link copied!'); }}
                  data-testid="copy-public-url-btn">
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => window.open(publicLeadUrl, '_blank')}
                  data-testid="open-public-url-btn">
                  Open
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{analytics.total_leads}</p>
              <p className="text-xs text-muted-foreground">Total Leads</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{analytics.stage_breakdown?.new || 0}</p>
              <p className="text-xs text-muted-foreground">New</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{analytics.stage_breakdown?.interested || 0}</p>
              <p className="text-xs text-muted-foreground">Interested</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{analytics.converted}</p>
              <p className="text-xs text-muted-foreground">Converted</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{analytics.conversion_rate}%</p>
              <p className="text-xs text-muted-foreground">Conversion Rate</p>
            </CardContent></Card>
          </div>
        )}

        {/* Kanban View */}
        {view === 'kanban' && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
              {stages.map(stage => (
                <div key={stage} className="min-w-[260px] flex-shrink-0">
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <div className={`w-2.5 h-2.5 rounded-full ${STAGE_COLORS[stage]}`} />
                    <span className="text-sm font-semibold">{STAGE_LABELS[stage]}</span>
                    <Badge variant="secondary" className="text-xs">{leadsByStage[stage]?.length || 0}</Badge>
                  </div>
                  <Droppable droppableId={stage}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-2 min-h-[200px] max-h-[60vh] overflow-y-auto pr-1 p-1 rounded ${snapshot.isDraggingOver ? 'bg-muted/40' : ''}`}
                        data-testid={`kanban-column-${stage}`}
                      >
                        {(leadsByStage[stage] || []).map((lead, index) => (
                          <Draggable key={lead.id} draggableId={lead.id} index={index}>
                            {(provided, snapshot) => (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`cursor-pointer hover:shadow-md transition-shadow ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}
                                onClick={() => openLeadDetail(lead)}
                                data-testid={`lead-card-${lead.id}`}
                              >
                                <CardContent className="p-3">
                                  <div className="flex items-start justify-between">
                                    <div className="min-w-0">
                                      <p className="font-medium text-sm truncate">{lead.first_name} {lead.last_name}</p>
                                      {lead.email && <p className="text-xs text-muted-foreground truncate">{lead.email}</p>}
                                      {lead.phone && <p className="text-xs text-muted-foreground">{lead.phone}</p>}
                                    </div>
                                    <Badge variant="outline" className="text-[10px] flex-shrink-0">{SOURCE_LABELS[lead.source] || lead.source}</Badge>
                                  </div>
                                  {lead.student_level && (
                                    <Badge variant="secondary" className="mt-1.5 text-[10px] capitalize">{lead.student_level}</Badge>
                                  )}
                                  {lead.assigned_to_name && (
                                    <p className="text-[10px] text-muted-foreground mt-1">Assigned: {lead.assigned_to_name}</p>
                                  )}
                                </CardContent>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {(!leadsByStage[stage] || leadsByStage[stage].length === 0) && (
                          <div className="text-center py-8 text-xs text-muted-foreground border border-dashed rounded-lg">No leads</div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        )}

        {/* List View */}
        {view === 'list' && (
          <DataGrid
            title="Leads"
            data-testid="leads-list"
            rows={leads}
            loading={loading}
            onRefresh={fetchLeads}
            searchKeys={['email', 'phone', 'first_name', 'last_name', 'guardian_name', 'notes']}
            headerExtra={viewSwitcher}
            advancedSearch={(
              <div className="flex items-end gap-3 flex-wrap">
                <div className="space-y-1">
                  <Label className="text-xs">Search server</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search leads..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9 w-64" data-testid="search-leads" />
                  </div>
                </div>
                {stageSourceFilters}
              </div>
            )}
            activeFilterCount={activeFilterCount}
            columns={[
              { key: 'name', header: 'Lead', accessor: (l) => `${l.first_name || ''} ${l.last_name || ''}`.trim(), className: 'font-medium' },
              { key: 'email', header: 'Email', accessor: (l) => l.email || '', className: 'text-muted-foreground' },
              { key: 'phone', header: 'Phone', width: 140, accessor: (l) => l.phone || '', className: 'text-muted-foreground' },
              { key: 'student_level', header: 'Level', width: 120, accessor: (l) => l.student_level || '', className: 'capitalize' },
              {
                key: 'stage', header: 'Stage', width: 170, accessor: (l) => l.stage,
                render: (l) => (
                  <div onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                    <Select value={l.stage} onValueChange={(v) => { handleStageChange(l.id, v); }}>
                      <SelectTrigger className="h-7 w-[150px] px-1.5 text-xs border-dashed bg-transparent" aria-label="Change stage" data-testid={`lead-stage-${l.id}`}>
                        <StatusBadge value={l.stage} />
                      </SelectTrigger>
                      <SelectContent>{stages.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ),
              },
              { key: 'source', header: 'Source', width: 120, accessor: (l) => SOURCE_LABELS[l.source] || l.source || '' },
              { key: 'assigned_to_name', header: 'Assigned to', width: 150, accessor: (l) => l.assigned_to_name || 'Unassigned', className: 'text-muted-foreground' },
              { key: 'created_at', header: 'Created', width: 120, render: (l) => new Date(l.created_at).toLocaleDateString() },
            ]}
            rowTone={(l) => {
              if (l.stage === 'lost') return 'danger';
              if (l.stage === 'enrolled' || l.converted) return 'success';
              if (l.stage === 'demo_scheduled') return 'warning';
              return null;
            }}
            onRowDoubleClick={(l) => openLeadDetail(l)}
            primaryActions={leadActions}
            selectionActions={[
              { key: 'open', label: 'Open', icon: Eye, onClick: ([l]) => openLeadDetail(l) },
              { key: 'assign', label: 'Assign', icon: UserCog, onClick: ([l]) => openLeadDetail(l), hidden: isInstructor },
              {
                key: 'convert', label: 'Convert to student', icon: UserCheck,
                onClick: ([l]) => {
                  if (l.converted) { toast.info('This lead has already been converted'); return; }
                  openLeadDetail(l);
                },
              },
              { key: 'delete', label: 'Delete', icon: Trash2, variant: 'destructive', onClick: ([l]) => handleDeleteLead(l.id), hidden: isInstructor },
            ]}
            emptyMessage="No leads found"
          />
        )}
      </Page>
      </SplitLayout>
    </DashboardLayout>
  );
}
